/**
 * The RPCs a signed-in client calls.
 *
 * Identity lives in `auth.ts`; the client itself in `client.ts`. This module is
 * only the request/response shapes.
 */

import type { Session } from '@heroiclabs/nakama-js';
import { client } from './client.js';

/** Nakama's JS client stringifies RPC input itself; pass the object. */
async function rpc(session: Session, name: string, input: unknown): Promise<unknown> {
  const response = await client.rpc(session, name, input as Record<string, unknown>);
  const payload = response.payload;
  return typeof payload === 'string' ? JSON.parse(payload) : payload;
}

export interface AvatarOption {
  id: string;
  label: string;
  emoji: string;
}

/** One thing a player can say at a table. The id is what travels. */
export interface ChatPhrase {
  id: string;
  text: string;
}

/** Where a browser should look to reach another browser. */
export interface IceServer {
  urls: string[];
  username?: string;
  credential?: string;
}

export interface ClientConfig {
  /** Whether this session is a real account rather than a guest. */
  account: { hasEmail: boolean };
  avatars: AvatarOption[];
  /**
   * The faces this account may wear, grouped for a picker.
   *
   * Narrower than `avatars` above, which is every face there is because a
   * client has to draw one somebody else is wearing whether it owns it or not.
   * This is "what may I choose for myself", which only the server can answer.
   */
  avatarGroups: { name: string; avatarIds: string[] }[];
  /** Everything a player may say. Sent so the client can render the words. */
  phrases: ChatPhrase[];
  name: { minLength: number; maxLength: number };
  turnLimitMs: { min: number; default: number; max: number };
  profile: { displayName: string; avatarId: string } | null;
  /**
   * Voice, and where to find a peer.
   *
   * `available` is false where the deployment has no STUN configured, and the
   * client should then offer no join button at all — one that never works is
   * worse than none. The credentials inside `iceServers` expire within the
   * hour, so they are fetched with the config rather than cached.
   */
  voice?: { available: boolean; iceServers: IceServer[] };
}

export function clientConfig(session: Session): Promise<ClientConfig> {
  return rpc(session, 'client_config', {}) as Promise<ClientConfig>;
}

export interface SetProfileResult {
  ok: boolean;
  profile?: { displayName: string; avatarId: string };
  message?: string;
}

export function setProfile(
  session: Session,
  displayName: string,
  avatarId: string,
): Promise<SetProfileResult> {
  return rpc(session, 'set_profile', { displayName, avatarId }) as Promise<SetProfileResult>;
}

export interface CreatedRoom {
  roomCode: string;
  /** The largest this table may be made, given what the host owns. */
  seatCap: number;
  gameId: string;
}

/**
 * Room settings that have to be decided before the room exists.
 *
 * Everything here can also be changed afterwards from the lobby, as a host
 * action — a room is opened before anybody has arrived, so these are opening
 * guesses rather than commitments. They are accepted here too so a host who
 * already knows what they want does not have to set it twice.
 */
export interface RoomSettings {
  seatCount: number;
  turnLimitMs?: number;
  autoContinue?: boolean;
  allowObservers?: boolean;
}

export function createRoom(
  session: Session,
  gameId: string,
  settings: RoomSettings,
): Promise<CreatedRoom> {
  return rpc(session, 'create_room', { gameId, ...settings }) as Promise<CreatedRoom>;
}

/**
 * What a client needs to render a game it has never heard of.
 *
 * `layout` and `zones` come from the server rather than being bundled here, so
 * adding a game is a server deploy rather than a server deploy and a client
 * deploy. That is the whole reason this renderer can stay generic.
 */
export interface VariantSpecWire {
  id: string;
  kind: 'toggle' | 'choice' | 'number';
  label: string;
  help?: string;
  options?: Array<{ value: string; label: string }>;
  min?: number;
  max?: number;
  step?: number;
  default: boolean | string | number;
}

export interface JoinedRoom {
  matchId: string;
  gameId: string;
  /** Sent with the join so a guest never fetches a catalogue to show a title. */
  gameName: string;
  layout: GameLayoutWire | null;
  zones: ZoneSpecWire[];
  /**
   * What this game OFFERS. Static for its lifetime, so it rides with the join
   * exactly as the layout does - which is what keeps adding a house rule a
   * server deploy rather than a client one.
   */
  variants: VariantSpecWire[];
  /** One line on what you are trying to do. */
  objective?: string;
  /** The same rules the game's detail page shows, for reading mid-game. */
  howToPlay?: string[];
}

export interface ZoneSpecWire {
  name: string;
  visibility: 'all' | 'owner' | 'countOnly' | 'none';
  perPlayer?: boolean;
  reveal?: 'full' | 'top';
}

export interface ZonePlacementWire {
  zone: string;
  shape: 'stack' | 'fan' | 'row' | 'count' | 'backs' | 'pyramid' | 'column';
  region: string;
  label?: string;
  /** Drawn instead of `label` when the game gives one; see the toolkit's note. */
  shortLabel?: string;
  /** A row read as one thing, which never wraps. See the toolkit's note. */
  oneLine?: boolean;
  /** Draw this pile at the small card size. See the toolkit's note. */
  small?: boolean;
  sort?: 'suit' | 'rank' | 'none';
  highlightOnTurn?: boolean;
  /** A face-down pile this one sits on, drawn as slivers beneath it. */
  under?: string;
  /** Where this sits on a board, in one-based grid cells. */
  row?: number;
  col?: number;
  span?: number;
  /** An empty one of these is still a place, so it is drawn empty. */
  target?: boolean;
}

/**
 * A move that names a seat, and how to draw it. See `seatActions` in the
 * toolkit's layout for why it is declared rather than worked out.
 */
export interface SeatActionWire {
  move: string;
  /** One or two characters, for the button on a ring seat. */
  mark: string;
  /** The sentence the mark stands for, for anybody not reading marks. */
  label: string;
  /** The words on the viewer's own copy of it, beside their hand. */
  ownLabel: string;
  /** While a seat has just reached `count` in `zone`, only it may press. */
  grace?: { zone: string; count: number; ms: number };
  graceNote?: string;
}

export interface GameLayoutWire {
  /** Moves that ask a question rather than naming a card. Declared per game. */
  prompts?: Array<{
    move: string;
    label: string;
    unit?: string;
    /** What pressing it does, so it can be coloured. See the toolkit's note. */
    tone?: 'go' | 'quit' | 'commit';
    /** What each argument is, so the client can draw it. Absent means "as before". */
    args?: Array<'text' | 'rank' | 'suit' | 'seat' | 'amount'>;
    /** The question for each argument after the first. `{name}` is the last answer. */
    then?: string[];
    /** The verb on the button that commits an `amount`. "Raise to", "Bet". */
    commit?: string;
    labels?: Record<string, string>;
  }>;
  gameId: string;
  table: ZonePlacementWire[];
  hand: ZonePlacementWire[];
  seatBadges?: string[];
  objective?: string;
  /** Ranks this game wrote on, and what they say; see the toolkit note. */
  cardMarks?: { [rank: string]: string };
  /** A per-seat running score the game names, rather than one we guess at. */
  seatScore?: { field: string; label?: string; lowIsBetter?: boolean };
  /** Money on the felt: the pot, and what each seat has put in. Toolkit's note. */
  chips?: { pot: string; committed?: string };
  /** Two per-seat numbers that read as one fact: "1 / 3" tricks. */
  seatTally?: { value: string; target: string; label?: string };
  /**
   * Seats that win and lose together, for games played in partnerships.
   *
   * `seats` names a view field holding one entry per team, each an array of
   * seat numbers; `score` names one holding a number per team. Absent for every
   * game where a chair is its own side, which is most of them.
   */
  teams?: { seats: string; score: string; label?: string; lowIsBetter?: boolean };
  /** A named suit that overrides what the top of a pile means. */
  declaredSuit?: { zone: string; field: string };
  /**
   * Card movements the table animates. `from`/`to` name a zone, or the literal
   * "seat" for whichever seat acted.
   */
  motions?: { move: string; from: string; to: string; faceDown?: boolean }[];
  /** A move whose one argument is a seat, drawn on the seats. */
  seatActions?: SeatActionWire[];
  /** What a move's button should say, where splitting its name reads wrong. */
  moveLabels?: Record<string, string>;
}

export function joinRoom(session: Session, roomCode: string): Promise<JoinedRoom> {
  return rpc(session, 'join_room', { roomCode }) as Promise<JoinedRoom>;
}

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

export interface CatalogGame {
  id: string;
  name: string;
  family: string;
  minPlayers: number;
  maxPlayers: number;
  free: boolean;
  blurb: string;
  description: string;
  howToPlay: string[];
  playTimeMinutes: number;
  /**
   * How hard it is TO LEARN - never how hard it is to play well.
   *
   * Optional on the wire so a client stays compatible with a server that has
   * not been redeployed yet: the chip is simply absent rather than the card
   * failing to render.
   */
  difficulty?: 'Easy' | 'Medium' | 'Hard';
  /**
   * A children's card game, in the genre sense - what a child is taught first.
   *
   * A claim about the game, never about who is allowed to play it: see the
   * field's own comment in the generated catalog for why that distinction is
   * the whole design, and `render/shelves.ts` for what it draws.
   *
   * Optional for the same reason `difficulty` is - a client can be newer than
   * the server. Against one that has not been redeployed no game carries it,
   * the shelf holds nothing, and `shelve` drops an empty shelf, so the page is
   * simply the page it was before rather than a heading over a gap.
   */
  childrensClassic?: boolean;
  /** Free, or bought. Either way it can be played. */
  owned: boolean;
  /** Largest table THIS account may open. Zero means it may not host the game. */
  maxSeats: number;
}

export interface CatalogPack {
  id: string;
  name: string;
  blurb: string;
  description: string;
  priceCents: number;
  gameIds: string[];
  playerUpgrade: boolean;
  /** Themes this pack grants. How the holiday pairs are sold. */
  themeIds: string[];
  owned: boolean;
}

/** A deck, a table and a cast, bought together (D87). */
export interface CatalogTheme {
  id: string;
  name: string;
  blurb: string;
  description: string;
  priceCents: number;
  /** Owned by everybody and never sold. */
  free: boolean;
  contents: { deckId: string; tableId: string; avatarIds: string[] };
  owned: boolean;
}

export interface Equipped {
  deckId: string;
  tableId: string;
}

export interface Catalog {
  currency: string;
  seatCaps: { free: number; upgraded: number };
  games: CatalogGame[];
  packs: CatalogPack[];
  themes: CatalogTheme[];
  /** What this account is wearing. The free theme before anybody chooses. */
  equipped: Equipped;
  entitlements: { playerUpgrade: boolean; gameIds: string[] };
}

export function catalog(session: Session): Promise<Catalog> {
  return rpc(session, 'catalog', {}) as Promise<Catalog>;
}

export interface PurchaseResult {
  ok: boolean;
  reason?: string;
  message?: string;
}

export function purchase(session: Session, packId: string): Promise<PurchaseResult> {
  return rpc(session, 'purchase', { packId }) as Promise<PurchaseResult>;
}

export function purchaseThemeRpc(session: Session, themeId: string): Promise<PurchaseResult> {
  return rpc(session, 'purchase_theme', { themeId }) as Promise<PurchaseResult>;
}

export interface EquipResult {
  ok: boolean;
  equipped?: Equipped;
  reason?: string;
  message?: string;
}

/**
 * Wear a deck, a table, or both.
 *
 * Partial on purpose - the server keeps whatever half is not named - so the
 * two pickers in Account can each send only what they changed rather than
 * having to know the other's current value.
 */
export function equipRpc(
  session: Session,
  wanted: { deckId?: string; tableId?: string },
): Promise<EquipResult> {
  return rpc(session, 'equip', wanted) as Promise<EquipResult>;
}

/** Integer minor units to something a person reads. */
export function formatPrice(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    // An unknown currency code should cost the symbol, not the price.
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export interface ChangePasswordResult {
  ok: boolean;
  reason?: string;
  message?: string;
}

/**
 * Change your own password while signed in.
 *
 * Separate from the reset flow on purpose: reset proves you own an inbox, this
 * proves you know the current password. Neither substitutes for the other.
 */
export function changePassword(
  session: Session,
  currentPassword: string,
  newPassword: string,
): Promise<ChangePasswordResult> {
  return rpc(session, 'change_password', {
    currentPassword,
    newPassword,
  }) as Promise<ChangePasswordResult>;
}
